import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
    Chip,
} from "@mui/material";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import { useUserList } from "../../../../common/API/hooks";
import moment from "moment";
import Back from "../../../../common/components/BackNavigateComponent";

function formatLastLogin(lastLogin) {
    if (!lastLogin) return "—";
    return moment(lastLogin).format("YYYY-MM-DD HH:mm");
}

export default function PeopleManagerAudit() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const { data: userList, isLoading } = useUserList();

    const filteredUsers = useMemo(() => {
        if (!userList) return [];
        if (!search.trim()) return userList;
        const q = search.toLowerCase();
        return userList.filter(
            (u) =>
                u.username?.toLowerCase().includes(q) ||
                u.first_name?.toLowerCase().includes(q) ||
                u.last_name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q)
        );
    }, [userList, search]);

    return (
        <Grid container>
            <Grid sx={{ mt: 3 }} display="flex" alignItems="center">
                <Back />
                <Typography variant="h5" sx={{ ml: 2 }}>
                    User Audit
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12} pt={2}>
                <Box
                    sx={{ backgroundColor: "#f6f6f8", borderRadius: ".5rem" }}
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="body2" color="text.secondary">
                        Select a user to view their dashboard usage history.
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Search users…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ backgroundColor: "#fff", minWidth: 260 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Grid>

            <Grid item xs={12} sm={12} mt={2}>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f6f6f8" }}>
                                <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Last Login</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Groups</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>
                                    Audit
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No users found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers.map((user) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <PersonIcon fontSize="small" sx={{ color: "#1976d2" }} />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {user.username}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {[user.first_name, user.last_name]
                                                .filter(Boolean)
                                                .join(" ") || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {user.email || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={user.last_login ? "text.primary" : "text.disabled"}
                                            >
                                                {formatLastLogin(user.last_login)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                {user.groups?.length > 0
                                                    ? user.groups.map((g) => (
                                                          <Tooltip key={g} title={g} arrow>
                                                              <Chip
                                                                  title={g}
                                                                  label={g}
                                                                  size="small"
                                                                  variant="outlined"
                                                                  sx={{
                                                                      maxWidth: 150,
                                                                      '& .MuiChip-label': {
                                                                          overflow: 'hidden',
                                                                          textOverflow: 'ellipsis',
                                                                          whiteSpace: 'nowrap',
                                                                      },
                                                                  }}
                                                              />
                                                          </Tooltip>
                                                      ))
                                                    : "—"}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={`View usage audit for ${user.username}`}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                        navigate(`/user-audit/${user.username}`)
                                                    }
                                                >
                                                    <ManageSearchIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {userList && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""} shown
                        {search ? ` (filtered from ${userList.length})` : ""}
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
}

